<script setup lang="ts">
import type { ShiftAssignment, ShiftSlot } from '@/types/employee';
import { formatDayHeader } from '@/utils/date';
import { formatTimeRange } from '@/utils/time';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import UserBadge from '../Profile/UserBadge.vue';

const { t, te } = useI18n();

const props = defineProps<{
  calendarData: Record<string, Record<string, ShiftAssignment[]>>;
  days: string[]; // e.g. ['2025-05-12', ...]
  timeSlots: ShiftSlot[];
  weekRange: { start: Date; end: Date };
  selectedSlots?: Array<{ day: string; slot: ShiftSlot }>;
}>();

const emit = defineEmits<{
  (e: 'slot-click', payload: { day: string; slot: ShiftSlot }): void;
  (e: 'slot-row-click', payload: { slot: ShiftSlot; days: string[] }): void;
}>();

function isSelected(day: string, slot: ShiftSlot) {
  return props.selectedSlots?.some((s) => s.day === day && s.slot.id === slot.id);
}

function handleSlotClick(slot: ShiftSlot) {
  emit('slot-row-click', { slot, days: props.days });
}

const today = computed(() => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
});
</script>

<template>
  <div class="overflow-x-auto">
    <!-- Time slots legend -->
    <div v-if="timeSlots.some((slot) => slot.start_time || slot.end_time)" class="mb-4 rounded-lg bg-gray-50 p-3">
      <h4 class="mb-2 text-sm font-medium text-gray-700">{{ t('employee.slots.timeRanges') }}</h4>
      <div class="flex flex-wrap gap-3">
        <div v-for="timeSlot in timeSlots" :key="`legend-${timeSlot.id}`" class="flex items-center gap-2 text-xs">
          <span class="font-medium">
            {{ te(`employee.slots.timeSlots.${timeSlot.name}`) ? t(`employee.slots.timeSlots.${timeSlot.name}`) : timeSlot.name }} :
          </span>
          <span v-if="timeSlot.start_time || timeSlot.end_time" class="text-gray-600">
            {{ formatTimeRange(timeSlot.start_time, timeSlot.end_time, t) }}
          </span>
          <span v-else class="italic text-gray-400">
            {{ t('employee.slots.noTimeSet') }}
          </span>
        </div>
      </div>
    </div>

    <div class="calendar-grid">
      <!-- Header row with day names -->
      <div class="grid border-b [grid-template-columns:repeat(22,minmax(0,1fr))]">
        <div class="col-span-1 min-w-4 border-r sm:min-w-6"></div>
        <div
          v-for="day in props.days"
          :key="day"
          class="col-span-3 border-r p-2 text-center text-xs font-medium last:border-r-0 sm:text-sm"
          :class="{ 'bg-accent text-white': day === today }"
        >
          <div class="flex flex-col">
            <span>{{ t(`common.dayShort.${formatDayHeader(day).dayShort}`) }}</span>
            <span class="text-xs font-normal opacity-75">{{ formatDayHeader(day).formatted.slice(0, -5) }}</span>
          </div>
        </div>
      </div>

      <!-- Time slots and scheduling grid -->
      <div v-for="timeSlot in timeSlots" :key="timeSlot.id" class="grid border-b [grid-template-columns:repeat(22,minmax(0,1fr))] last:border-b-0">
        <!-- Time slot label -->
        <div
          class="col-span-1 flex min-w-4 items-center justify-center border-r hover:cursor-pointer sm:min-w-6 xl:hover:bg-primary-50"
          @click="handleSlotClick(timeSlot)"
        >
          <span class="vertical-text text-xs font-medium sm:text-sm">{{
            te(`employee.slots.timeSlots.${timeSlot.name}`) ? t(`employee.slots.timeSlots.${timeSlot.name}`) : timeSlot.name
          }}</span>
        </div>
        <!-- Day cells -->
        <div
          v-for="day in props.days"
          :key="`${timeSlot}-${day}`"
          class="col-span-3 flex min-h-[80px] cursor-pointer flex-wrap items-start justify-start gap-2 border-r p-2 last:border-r-0 xl:hover:bg-primary-200"
          :class="{ 'bg-primary-100': isSelected(day, timeSlot) }"
          @click="emit('slot-click', { day, slot: timeSlot })"
        >
          <div v-for="userShift in calendarData[day]?.[timeSlot.name] || []" :key="userShift.restaurant_user_id">
            <UserBadge
              :user="userShift.user"
              :position="userShift.kitchen_position"
              :color="userShift.kitchen_position?.color"
              :accent="!!(userShift.start_time || userShift.end_time)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.calendar-grid {
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  overflow: hidden;
}

.vertical-text {
  writing-mode: vertical-lr;
  text-orientation: mixed;
  transform: rotate(180deg);
}
</style>
